import type {
  CustomerProfileIdentity,
  CustomerProfileScope,
  ProfileActivityPoint,
  ProfileDeviceGeo,
  ProfileMetrics,
  ProfileRange,
  ProfileReferrer,
  ProfileSessionsResponse,
  ProfileTimeseriesPoint,
  ProfileTopPage,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

export type {
  CustomerProfileIdentity,
  CustomerProfileScope,
  ProfileActivityPoint,
  ProfileDeviceGeo,
  ProfileDeviceGeoCurrent,
  ProfileMetrics,
  ProfileRange,
  ProfileReferrer,
  ProfileSession,
  ProfileSessionsResponse,
  ProfileTimeseriesPoint,
  ProfileTopPage,
} from '@logspot-platform/platform-types';

// Profile analytics live on two surfaces backed by the same query shapes:
//
//  - Admin (dashboard, session-cookie auth): `/admin/identities/:id/profile-*`
//    — same `api` client + `/api` base path as `identities-service.ts`. Scoped
//    by the resolved identity.
//  - Customer (project-scoped, session auth): `/profile/*` addressed by
//    `projectId + userId` — same `api` client + `/api` base path as
//    `analytics-service.ts`. The backend hides identity resolution here, so an
//    unknown user never 404s; it renders zeros.
//
// `from`/`to` are ISO strings; omitting them lets the backend default to the
// last 30 days. The activity calendar takes a fixed 365-day window (no range).

// ---------- Helpers ----------

type SuccessEnvelope<T> = { data: T };

const appendRange = (params: URLSearchParams, range?: ProfileRange): void => {
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
};

// ---------- Admin service (identity-scoped) ----------

export const AdminProfileService = {
  getMetrics: async (
    identityId: string,
    range?: ProfileRange,
  ): Promise<ProfileMetrics> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileMetrics>>(
      `/admin/identities/${identityId}/profile-metrics${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  getActivity: async (
    identityId: string,
  ): Promise<ProfileActivityPoint[]> => {
    const result = await api.get<SuccessEnvelope<ProfileActivityPoint[]>>(
      `/admin/identities/${identityId}/profile-activity`,
    );
    return result.data;
  },

  getDeviceGeo: async (
    identityId: string,
    range?: ProfileRange,
  ): Promise<ProfileDeviceGeo> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileDeviceGeo>>(
      `/admin/identities/${identityId}/profile-device-geo${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  getTopPages: async (
    identityId: string,
    range?: ProfileRange & { limit?: number },
  ): Promise<ProfileTopPage[]> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    if (range?.limit) params.set('limit', String(range.limit));
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileTopPage[]>>(
      `/admin/identities/${identityId}/profile-top-pages${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  getReferrers: async (
    identityId: string,
    range?: ProfileRange & { limit?: number },
  ): Promise<ProfileReferrer[]> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    if (range?.limit) params.set('limit', String(range.limit));
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileReferrer[]>>(
      `/admin/identities/${identityId}/profile-referrers${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  getTimeseries: async (
    identityId: string,
    range?: ProfileRange,
  ): Promise<ProfileTimeseriesPoint[]> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileTimeseriesPoint[]>>(
      `/admin/identities/${identityId}/profile-timeseries${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },

  getSessions: async (
    identityId: string,
    range?: ProfileRange & { cursor?: string | null; limit?: number },
  ): Promise<ProfileSessionsResponse> => {
    const params = new URLSearchParams();
    appendRange(params, range);
    if (range?.cursor) params.set('cursor', range.cursor);
    if (range?.limit) params.set('limit', String(range.limit));
    const qs = params.toString();
    const result = await api.get<SuccessEnvelope<ProfileSessionsResponse>>(
      `/admin/identities/${identityId}/profile-sessions${qs ? `?${qs}` : ''}`,
    );
    return result.data;
  },
};

// ---------- Customer service (project + user scoped) ----------

const customerParams = (
  { projectId, userId }: CustomerProfileScope,
  range?: ProfileRange,
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('projectId', projectId);
  params.set('userId', userId);
  appendRange(params, range);
  return params;
};

export const CustomerProfileService = {
  getIdentity: async (
    scope: CustomerProfileScope,
  ): Promise<CustomerProfileIdentity> => {
    const params = customerParams(scope);
    const result = await api.get<SuccessEnvelope<CustomerProfileIdentity>>(
      `/profile/identity?${params.toString()}`,
    );
    return result.data;
  },

  getMetrics: async (
    scope: CustomerProfileScope,
    range?: ProfileRange,
  ): Promise<ProfileMetrics> => {
    const params = customerParams(scope, range);
    const result = await api.get<SuccessEnvelope<ProfileMetrics>>(
      `/profile/metrics?${params.toString()}`,
    );
    return result.data;
  },

  getActivity: async (
    scope: CustomerProfileScope,
  ): Promise<ProfileActivityPoint[]> => {
    const params = customerParams(scope);
    const result = await api.get<SuccessEnvelope<ProfileActivityPoint[]>>(
      `/profile/activity?${params.toString()}`,
    );
    return result.data;
  },

  getDeviceGeo: async (
    scope: CustomerProfileScope,
    range?: ProfileRange,
  ): Promise<ProfileDeviceGeo> => {
    const params = customerParams(scope, range);
    const result = await api.get<SuccessEnvelope<ProfileDeviceGeo>>(
      `/profile/device-geo?${params.toString()}`,
    );
    return result.data;
  },

  getTopPages: async (
    scope: CustomerProfileScope,
    range?: ProfileRange & { limit?: number },
  ): Promise<ProfileTopPage[]> => {
    const params = customerParams(scope, range);
    if (range?.limit) params.set('limit', String(range.limit));
    const result = await api.get<SuccessEnvelope<ProfileTopPage[]>>(
      `/profile/top-pages?${params.toString()}`,
    );
    return result.data;
  },

  getReferrers: async (
    scope: CustomerProfileScope,
    range?: ProfileRange & { limit?: number },
  ): Promise<ProfileReferrer[]> => {
    const params = customerParams(scope, range);
    if (range?.limit) params.set('limit', String(range.limit));
    const result = await api.get<SuccessEnvelope<ProfileReferrer[]>>(
      `/profile/referrers?${params.toString()}`,
    );
    return result.data;
  },

  getTimeseries: async (
    scope: CustomerProfileScope,
    range?: ProfileRange,
  ): Promise<ProfileTimeseriesPoint[]> => {
    const params = customerParams(scope, range);
    const result = await api.get<SuccessEnvelope<ProfileTimeseriesPoint[]>>(
      `/profile/timeseries?${params.toString()}`,
    );
    return result.data;
  },

  getSessions: async (
    scope: CustomerProfileScope,
    range?: ProfileRange & { cursor?: string | null; limit?: number },
  ): Promise<ProfileSessionsResponse> => {
    const params = customerParams(scope, range);
    if (range?.cursor) params.set('cursor', range.cursor);
    if (range?.limit) params.set('limit', String(range.limit));
    const result = await api.get<SuccessEnvelope<ProfileSessionsResponse>>(
      `/profile/sessions?${params.toString()}`,
    );
    return result.data;
  },
};
